import _sortBy from 'lodash/sortBy';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PlatformType, SnackBarType } from '../../app/types';
import { randomText } from '../../app/utils';
import {
  changeMessageCheckCondition,
  setDefaultTemplate,
  setNodeData,
  setState,
} from '../../features/mainSlice';
import { enqueueSnackbar } from '../../features/notifierSlice';
import useDetectScrollBottom from '../../hooks/useDetectScrollBottom';
import OrganizationService from '../../services/OrganizationService';
import AlertModal from '../shared/AlertModal';
import DefaultTemplate from './DefaultTemplate';

let lastGetdataLength = 0;
const limit = 20;

function DefaultTemplateContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { objectId: orgId } = useSelector(store => store.organization);
  const { selectedNode, data: maData } = useSelector(store => store.ma);
  const { messages } = selectedNode?.data || {};
  const { platform } = maData || {};

  const globalTemplateType = useSelector(
    store => store.ma.defaultTemplate.templateType,
  );
  const messageIndex = useSelector(
    store => store.ma.defaultTemplate.messageIndex,
  );
  const [list, setList] = useState([]);
  const [localKeyword, setLocalKeyword] = useState('');
  const isTouchBottom = useDetectScrollBottom('default-template-content');
  const [localIsTouchBottom, setLocalIsTouchBottom] = useState(false);
  const [isLazyloading, setIsLazyloading] = useState(false);
  const [localTemplateType, setLocalTemplateType] =
    useState(globalTemplateType);
  const [selectData, setSelectData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const lineTemplateType = ['card', 'confirm', 'image'];
  const allNotLineTemplateType = ['card', 'confirm'];
  const whatsappTemplateType = ['card', 'confirm', 'waList'];

  useEffect(() => {
    async function query() {
      setIsLoadingTemplate(true);
      const response = await OrganizationService.getDefaultTemplates(
        orgId,
        globalTemplateType !== 'all'
          ? globalTemplateType
          : platform === PlatformType.LINE
          ? { $in: lineTemplateType }
          : platform === PlatformType.WHATSAPP
          ? { $in: whatsappTemplateType }
          : { $in: allNotLineTemplateType },
      );
      lastGetdataLength = response.result.length;
      setList(response.result);
      setIsLoadingTemplate(false);
    }

    try {
      query();
      setLocalTemplateType(globalTemplateType);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error ?? error.message,
        }),
      );
    }
  }, [globalTemplateType, platform]);

  useEffect(() => {
    setLocalIsTouchBottom(isTouchBottom);
  }, [isTouchBottom]);

  useEffect(() => {
    async function query() {
      if (!isLazyloading) {
        setIsLazyloading(true);
        const response = await OrganizationService.getDefaultTemplates(
          orgId,
          localTemplateType !== 'all'
            ? localTemplateType
            : platform === PlatformType.LINE
            ? { $in: lineTemplateType }
            : platform === PlatformType.WHATSAPP
            ? { $in: whatsappTemplateType }
            : { $in: allNotLineTemplateType },
          {
            $or: [
              {
                name:
                  localKeyword === ''
                    ? undefined
                    : { $regex: localKeyword, $options: 'i' },
              },
              {
                note:
                  localKeyword === ''
                    ? undefined
                    : { $regex: localKeyword, $options: 'i' },
              },
            ],
          },
          {
            skip: list.length,
          },
        );
        lastGetdataLength = response.result.length;
        setList([...list, ...response.result]);
        setIsLazyloading(false);
      }
    }

    if (localIsTouchBottom && isTouchBottom && lastGetdataLength >= limit) {
      setLocalIsTouchBottom(false);
      query();
    }
  }, [isTouchBottom, localIsTouchBottom, localTemplateType, platform]);

  async function handleOnEnter(keyword, templateType) {
    const type =
      templateType !== 'all'
        ? templateType
        : platform === PlatformType.LINE
        ? { $in: lineTemplateType }
        : platform === PlatformType.WHATSAPP
        ? { $in: whatsappTemplateType }
        : { $in: allNotLineTemplateType };

    try {
      setIsLoadingTemplate(true);

      const response = await OrganizationService.getDefaultTemplates(
        orgId,
        type,
        {
          $or: [
            {
              name: { $regex: keyword, $options: 'i' },
            },
            {
              note: { $regex: keyword, $options: 'i' },
            },
          ],
        },
      );
      lastGetdataLength = response.result.length;

      setList(response.result);
      setIsLoadingTemplate(false);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error ?? error.message,
        }),
      );
    }
  }

  function handleOnChange(keyword) {
    setLocalKeyword(keyword);
    if (keyword === '') {
      handleOnEnter(keyword, localTemplateType);
    }
  }

  function handleOnChangeType(templateType) {
    setLocalTemplateType(templateType);
    handleOnEnter(localKeyword, templateType);
  }

  function handleOnClose() {
    dispatch(
      setDefaultTemplate({
        isShow: false,
      }),
    );
  }

  function handleSelectDefaultTemplate(data) {
    const templateType = data?.templateType || data?.data?.templateType;
    dispatch(
      changeMessageCheckCondition(selectedNode?.id, () => {
        dispatch(
          setDefaultTemplate({
            isShow: false,
          }),
        );

        dispatch(
          setNodeData({
            messages: messages.map((msg, index) => {
              if (index === messageIndex) {
                return {
                  name: data.name,
                  contentType: 'application/x-template',
                  template: randomText(),
                  data: {
                    templateType,
                    elements: data?.data?.elements.map(elem => {
                      if (templateType === 'waList') {
                        return {
                          ...elem,
                          buttons: _sortBy(elem?.buttons, ['sectionIndex']),
                        };
                      }
                      return elem;
                    }),
                  },
                  index,
                };
              }
              return msg;
            }),
          }),
        );
      }),
    );
  }

  function handleEditDefaultTemplate(data) {
    dispatch(
      changeMessageCheckCondition(selectedNode?.id, () => {
        dispatch(
          setDefaultTemplate({
            isShow: false,
          }),
        );

        dispatch(
          setNodeData({
            messages: messages.map((msg, index) => {
              if (index === messageIndex) {
                return {
                  name: data.name,
                  templateId: data._id,
                  templateExpiredAt: data?.expiredAt,
                  contentType: 'application/x-template',
                  template: randomText(),
                  data: {
                    templateType:
                      data?.templateType || data?.data?.templateType,
                    elements: data.data.elements,
                  },
                  index,
                };
              }
              return msg;
            }),
          }),
        );

        dispatch(
          setState({
            isShowOptionalDialog:
              data?.templateType === 'card' ||
              data?.data?.templateType === 'card',
            isShowConfirmCardDialog:
              data?.templateType === 'confirm' ||
              data?.data?.templateType === 'confirm',
            isShowImageCardDialog:
              data?.templateType === 'image' ||
              data?.data?.templateType === 'image',
            isShowWaListDialog:
              data?.templateType === 'waList' ||
              data?.data?.templateType === 'waList',
            isSaveAsDefaultTemplate: true,
          }),
        );
      }),
    );
  }

  async function handleOnDeleteTemplateClick() {
    try {
      setIsLoading(true);
      await OrganizationService.deleteDefaultTemplate(orgId, selectData._id);
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: t('delete-default-template-success', {
            name: selectData?.name,
          }),
        }),
      );
      const response = await OrganizationService.getDefaultTemplates(
        orgId,
        localTemplateType !== 'all'
          ? localTemplateType
          : platform === PlatformType.LINE
          ? { $in: lineTemplateType }
          : platform === PlatformType.WHATSAPP
          ? { $in: whatsappTemplateType }
          : { $in: allNotLineTemplateType },
        {
          $or: [
            {
              name:
                localKeyword === ''
                  ? undefined
                  : { $regex: localKeyword, $options: 'i' },
            },
            {
              note:
                localKeyword === ''
                  ? undefined
                  : { $regex: localKeyword, $options: 'i' },
            },
          ],
        },
      );
      setList(response.result);
      setIsLoading(false);
      setIsOpen(false);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error ?? error.message,
        }),
      );
    }
  }

  function handleDeleteDefaultTempate(data) {
    setSelectData(data);
    setIsOpen(true);
  }

  return (
    <>
      <DefaultTemplate
        localKeyword={localKeyword}
        list={list}
        onEnter={keyword => {
          handleOnEnter(keyword, localTemplateType);
        }}
        onChange={handleOnChange}
        onChangeType={handleOnChangeType}
        templateType={localTemplateType}
        onClose={handleOnClose}
        onSelectDefaultTemplate={handleSelectDefaultTemplate}
        onEditDefaultTemplate={handleEditDefaultTemplate}
        onDeleteDefaultTemplate={handleDeleteDefaultTempate}
        platform={platform}
        isLoading={isLoadingTemplate}
        isEdit
        isSend
        isCopy
      />
      <AlertModal
        isOpen={isOpen}
        title={t('delete-default-template-title')}
        content={t('delete-default-template', {
          name: selectData?.name,
        })}
        onClick={handleOnDeleteTemplateClick}
        onCancel={() => setIsOpen(false)}
        isLoading={isLoading}
      />
    </>
  );
}

export default DefaultTemplateContainer;
